import { Grid, Column, Button, InlineNotification } from '@carbon/react'
import { Building, Calendar } from '@carbon/icons-react'
import { PeriodSelector } from '../shared'
import { EstablishmentMetricsCards } from './EstablishmentMetricsCards'
import { EstablishmentDailyActivityChart } from './EstablishmentDailyActivityChart'
import { EstablishmentTopActivitiesTable } from './EstablishmentTopActivitiesTable'
import { EstablishmentClassesTable } from './EstablishmentClassesTable'
import { EstablishmentGradesChart } from './EstablishmentGradesChart'
import styles from './EstablishmentAnalytics.module.scss'
import { TopActivitiesResponseDto } from '@sphere/analytics/domain'

export interface EstablishmentAnalyticsDashboardProps {
  summary: {
    total_users: number
    total_classes: number
    total_activities: number
    total_attempts: number
    avg_score: number | null
    passed_attempts: number
    total_time_ms: number
  }
  dailyActivity: Array<{
    date: string
    attempt_count: number
    active_users: number
    avg_score: number
  }>
  topActivities: TopActivitiesResponseDto
  classes: Array<{
    class_id: string
    student_count: number
    unique_activities: number
    total_attempts: number
    avg_score: number
    passed_attempts: number
    success_rate: number
  }>
  grades: Array<{
    grade: string
    student_count: number
    unique_activities: number
    total_attempts: number
    avg_score: number
    passed_attempts: number
    success_rate: number
  }>
  selectedPeriod: string
  onPeriodChange: (period: string) => void
  onRefresh: () => void
  health?: string | null
  error?: string | null
}

export function EstablishmentAnalyticsDashboard({
  summary,
  dailyActivity,
  topActivities,
  classes,
  grades,
  selectedPeriod,
  onPeriodChange,
  onRefresh,
  health,
  error,
}: EstablishmentAnalyticsDashboardProps) {
  return (
    <div className={styles.dashboardContainer}>
      <Grid narrow>
        {/* Header */}
        <Column lg={16} md={8} sm={4}>
          <div className={styles.header}>
            <div className={styles.headerContent}>
              <div className={styles.titleRow}>
                <Building size={32} className={styles.titleIcon} />
                <h1 className={styles.pageTitle}>Activités Établissement</h1>
              </div>
              <p className={styles.pageDescription}>
                Tableau de bord des performances de l'établissement
              </p>
              {health && (
                <div className={styles.healthStatus}>
                  <div className={styles.healthIndicator} />
                  Service opérationnel
                </div>
              )}
              {error && (
                <InlineNotification
                  kind="error"
                  title="Erreur"
                  subtitle={error}
                  lowContrast
                  className={styles.errorNotification}
                />
              )}
            </div>

            <div className={styles.headerActions}>
              <PeriodSelector
                value={selectedPeriod}
                onChange={onPeriodChange}
                id="period-select"
              />
              <Button renderIcon={Calendar} kind="tertiary" onClick={onRefresh}>
                Actualiser
              </Button>
            </div>
          </div>
        </Column>

        {/* Metrics Cards */}
        <Column lg={16} md={8} sm={4}>
          <EstablishmentMetricsCards
            totalUsers={summary.total_users}
            totalClasses={summary.total_classes}
            totalActivities={summary.total_activities}
            totalAttempts={summary.total_attempts}
            avgScore={summary.avg_score}
            passedAttempts={summary.passed_attempts}
          />
        </Column>

        {/* Daily Activity Chart */}
        <Column lg={16} md={8} sm={4}>
          <div className={styles.section}>
            <EstablishmentDailyActivityChart data={dailyActivity} />
          </div>
        </Column>

        {/* Top Activities */}
        <Column lg={16} md={8} sm={4}>
          <div className={styles.section}>
            <EstablishmentTopActivitiesTable topActivities={topActivities} />
          </div>
        </Column>

        {/* Classes Table */}
        <Column lg={16} md={8} sm={4}>
          <div className={styles.section}>
            <EstablishmentClassesTable classes={classes} />
          </div>
        </Column>

        {/* Grades Chart */}
        <Column lg={16} md={8} sm={4}>
          <div className={styles.section}>
            <EstablishmentGradesChart grades={grades} />
          </div>
        </Column>
      </Grid>
    </div>
  )
}
